import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, query, where, onSnapshot, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { RefreshCw, Search } from 'lucide-react';

const ArchivedCustomers = ({ currentUserProfile }) => {
    const [archived, setArchived] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;

        const q = query(
            collection(db, `organizations/${currentUserProfile.organizationId}/customers`),
            where('status', '==', 'Archived')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setArchived(data);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [currentUserProfile]);

    const handleRestore = async (customerId, name) => {
        if (!window.confirm(`Restore "${name}" to the active customer list?`)) return;

        try {
            await updateDoc(doc(db, `organizations/${currentUserProfile.organizationId}/customers`, customerId), {
                status: 'Active',
                restoredAt: serverTimestamp(),
                restoredBy: currentUserProfile.uid || 'Admin'
            });
        } catch (error) {
            console.error("Error restoring customer:", error);
            alert("Failed to restore customer.");
        }
    };

    const filtered = archived.filter(c => 
        (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.email || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                Archived Customers
            </h2>
            <p className="text-gray-600 mb-6 text-sm">
                These customers are hidden from standard lists but preserve their historical data (Invoices/Jobs).
            </p>

            <div className="mb-4 relative max-w-md">
                <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <input 
                    type="text" 
                    placeholder="Search archives..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 p-2 border rounded-md w-full"
                />
            </div>

            {loading ? (
                <div className="text-center py-8 text-gray-500">Loading archives...</div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded border border-dashed text-gray-500">
                    No archived customers found.
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-gray-700 uppercase font-semibold">
                            <tr>
                                <th className="px-4 py-3">Name</th>
                                <th className="px-4 py-3">Email</th>
                                <th className="px-4 py-3">Archived Date</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {filtered.map(customer => (
                                <tr key={customer.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 font-medium">{customer.name}</td>
                                    <td className="px-4 py-3 text-gray-500">{customer.email}</td>
                                    <td className="px-4 py-3 text-gray-500">
                                        {customer.archivedAt?.toDate().toLocaleDateString() || 'N/A'}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button 
                                            onClick={() => handleRestore(customer.id, customer.name)}
                                            className="text-blue-600 hover:text-blue-800 font-medium flex items-center justify-end gap-1 ml-auto"
                                        >
                                            <RefreshCw size={14} /> Restore
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ArchivedCustomers;